import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { utils, comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import {
    useFetch,
    useForm,
    useGrid,
    useStore,
    useToast,
    useResource,
    useModal,
    usePage,
    usePopup,
    useTheme,
} from "@/comn/hooks";
import {
    APIS,
    SF_USR_APLY_INFO,
    URLS,
    SF_LCNE_USR_INFO,
} from "@/tz/go/egaz/osci/ptl/usr/info/services/OsciPwdChgService";
import { useLogin } from "@/comn/features/authentication/useLogin";

import { getNameOfJSDocTypedef } from "typescript";
// 진입 시 비밀번호 인증 팝업 제거 : 화면의 Current Password 입력값으로 저장 시 서버에서 동일하게 검증함
// import { usePwdAuth } from "@/comn/hooks/usePwdAuth";

/*
 * Password Change
 * !== 비밀번호 변경 ==!
 */
export const OSCI0103004S = (): JSX.Element => {
    const {} = usePage();
    const { t } = useTranslation();
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { theme } = useTheme();
    const toast = useToast();

    // usePwdAuth();

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0083" },
            { area: "comnCd", comnCd: "OSC0012" },
            { area: "comnCd", comnCd: "OSC0013" },
            { area: "cstmCd" },
            { area: "cntyCd" },
            { area: "comnCd", comnCd: "OSC0147" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // User Information !== 사용자(대표자) 정보 ==!
        myInfo: useForm({
            defaultSchema: SF_USR_APLY_INFO,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get My Information !== 나의 정보 조회 ==!
        getMyInfo: useFetch({
            api: () => APIS.getMyInfo(theme.lang),
            onSuccess: (data) => {
                const myInfo = data.myInfo.content;
                form.myInfo.reset();
                form.myInfo.setValues(myInfo);
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                modal.openModal({
                    content: data.errorMessage,
                });
            },
            showToast: true,
        }),
        // Save My Information !== 나의 정보 수정 ==!
        saveMyInfo: useFetch({
            api: (data) => APIS.savePwd({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00226", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => fetch.getMyInfo.fetch(),
                    close: false,
                });
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.myInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),
    };

    const render = {};

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        checkUsrPwd: () => {
            const usrPwd = form.myInfo.getValue("usrPwd");
            const usrPwdCfrm = form.myInfo.getValue("usrPwdCfrm");

            if (!comnUtils.isEmpty(usrPwd) && !comnUtils.equals(usrPwd, usrPwdCfrm)) {
                form.myInfo.setError("usrPwdCfrm", {
                    message: "msg.osc.00210", // Passwords do not match. !== 비밀번호가 일치하지 않습니다. ==!
                    type: "error",
                });
            } else {
                form.myInfo.clearErrors("usrPwdCfrm");
            }
        },

        // on Save Button Click Event Handler  !== 알림 등록 ==!
        onSaveBtnClick: () => {
            if (!comnUtils.isEmpty(form.myInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }

            form.myInfo.handleSubmit(
                () => {
                    const data = form.myInfo.getValues();
                    modal.openModal({
                        content: "msg.osc.00090", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => fetch.saveMyInfo.fetch(data),
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
    };

    useEffect(() => {
        fetch.getMyInfo.fetch();
    }, [theme.lang]);

    return (
        <Page>
            <form>
                <Group>
                    <Group.Header>
                        <Group.Title title={t("T_MY_INFO")} />
                    </Group.Header>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                {/* <Group.Control {...form.myInfo.schema.usrIdntfNo}></Group.Control> */}
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.myInfo.schema.curPwdCfrm}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.myInfo.schema.usrPwd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control
                                    {...form.myInfo.schema.usrPwdCfrm}
                                    onBlur={() => {
                                        handler.checkUsrPwd();
                                    }}
                                />
                            </Group.Row>
                            {/* Display the password policy before saving a new password. */}
                            <Group.Row>
                                <Group.Any anySize={12} align="left">
                                    <span className="text-sm text-gray-500">{t("msg.osc.00225")}</span>
                                </Group.Any>
                            </Group.Row>
                        </Group.Section>
                        <Layout direction="row">
                            <Layout.Right>
                                <Button
                                    role={"save"}
                                    onClick={() => {
                                        handler.onSaveBtnClick();
                                    }}
                                ></Button>
                            </Layout.Right>
                        </Layout>
                    </Group.Body>
                </Group>
            </form>
        </Page>
    );
};
